import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { fetchFaqData } from '../api/services';
import { useFetch } from '../hooks';
import { useFaqCache } from '../store/AppContext';
import { LoadingSpinner, ErrorDisplay, SectionHeader } from '../components/common';
import { DESIGN_TOKENS } from '../styles/theme';

// ============================================================
// FAQ ROW COMPONENT
// ============================================================

interface FaqAccordionItemProps {
  question: string;
  answer: string;
  itemIndex: number;
}

function FaqAccordionItem({ question, answer, itemIndex }: FaqAccordionItemProps) {
  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: DESIGN_TOKENS.colors.primary }} />}
        aria-controls={`faq-panel-${itemIndex}-content`}
        id={`faq-panel-${itemIndex}-header`}
        sx={{
          px: 3,
          py: 0.5,
          '&.Mui-expanded': {
            backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HelpOutlineIcon
            sx={{ fontSize: 18, color: DESIGN_TOKENS.colors.primary, flexShrink: 0 }}
          />
          <Typography fontWeight={600} sx={{ lineHeight: 1.4 }}>
            {question}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pb: 3, pt: 2 }}>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.7, pl: 3.5 }}
        >
          {answer}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

// ============================================================
// FAQ PAGE COMPONENT
// ============================================================

export default function FaqPage() {
  const { cachedFaqData, cacheFaqData } = useFaqCache();

  const { data: fetchedFaqData, isLoading, error } = useFetch(
    fetchFaqData,
    [],
    { enabled: !cachedFaqData }
  );

  // Cache data when it arrives from API
  React.useEffect(() => {
    if (fetchedFaqData?.data && !cachedFaqData) {
      cacheFaqData(fetchedFaqData.data);
    }
  }, [fetchedFaqData, cachedFaqData, cacheFaqData]);

  const displayedFaqData = cachedFaqData ?? fetchedFaqData?.data ?? null;

  if (isLoading) {
    return <LoadingSpinner message="Ładowanie pytań i odpowiedzi..." fullHeight />;
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <ErrorDisplay message={error} />
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      {/* Header band */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          py: 7,
        }}
      >
        <Container maxWidth="md">
          <SectionHeader
            title={displayedFaqData?.title ?? 'Często zadawane pytania'}
            subtitle="Znajdź odpowiedzi na najczęściej zadawane pytania dotyczące naszej platformy."
            centered
          />
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        {displayedFaqData?.rows && displayedFaqData.rows.length > 0 ? (
          <Box>
            {displayedFaqData.rows.map((faqRow, rowIndex) => (
              <FaqAccordionItem
                key={`faq-${rowIndex}-${faqRow.title}`}
                question={faqRow.title}
                answer={faqRow.content}
                itemIndex={rowIndex}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 8 }}>
            Brak dostępnych pytań i odpowiedzi.
          </Typography>
        )}

        {/* Contact CTA */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
            borderRadius: DESIGN_TOKENS.borderRadius.card,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Nie znalazłeś odpowiedzi?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Skontaktuj się z nami bezpośrednio — odpiszemy w ciągu 24 godzin.
          </Typography>
          <Typography
            component="a"
            href="mailto:kontakt@qproconnect.com"
            sx={{
              display: 'inline-block',
              mt: 2,
              fontWeight: 600,
              color: DESIGN_TOKENS.colors.primary,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            kontakt@qproconnect.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
